import { EmptyState, ErrorState, LoadingState } from "@elyxeer/ui";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { PlanCard } from "~/components/membership/PlanCard";
import { useProductPage } from "~/components/membership/use-product-page";
import { useLocale } from "~/i18n";
import { fetchOfferings } from "~/lib/api";
import { queryKeys } from "~/lib/query-client";
import type { OfferingProduct } from "~/types/shop";

/**
 * The live shop: real Shopify offerings on the two plan cards, each handing over to
 * the product page on elyxeer.com — the app never assembles a checkout or renders a
 * payment step. Shown on the membership tab to app-only gift recipients, who may add
 * the physical subscription.
 *
 * Renders as a fragment: the parent owns the column and its gap.
 */
export function OfferingsSection() {
  const { t } = useTranslation();
  const { formatCurrency } = useLocale();
  const openProductPage = useProductPage();

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: queryKeys.shopOfferings,
    queryFn: ({ signal }) => fetchOfferings(signal),
  });

  return (
    <>
      {isPending ? <LoadingState /> : null}
      {isError ? (
        <ErrorState
          message={
            error instanceof Error
              ? error.message
              : t("membership.shopUnavailable")
          }
          onRetry={() => void refetch()}
        />
      ) : null}
      {!isPending && !isError && (data?.items.length ?? 0) === 0 ? (
        <Box data-testid="membership.offerings-empty">
          <EmptyState message={t("membership.shopUnavailable")} />
        </Box>
      ) : null}

      {(data?.items ?? []).map((product) => (
        <OfferingCards
          key={product.id}
          product={product}
          formatCurrency={formatCurrency}
          onOpen={() => void openProductPage(product.handle)}
        />
      ))}

      {(data?.items.length ?? 0) > 0 ? (
        <Typography variant="T3" sx={{ color: "text.muted" }}>
          {t("membership.checkout.note")}
        </Typography>
      ) : null}
    </>
  );
}

function OfferingCards({
  product,
  formatCurrency,
  onOpen,
}: {
  product: OfferingProduct;
  formatCurrency: (amount: string, currencyCode: string) => string;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const variant = product.variants[0];
  if (!variant) return null;
  const plan = variant.selling_plans[0];

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        alignItems: "start",
        gap: { xs: 2, md: 3 },
      }}
    >
      {plan ? (
        <PlanCard
          label={t("membership.plans.subscription.label")}
          title={t("membership.plans.subscription.title")}
          description={t("membership.plans.subscription.description")}
          price={formatCurrency(
            plan.price_per_delivery.amount,
            plan.price_per_delivery.currency_code,
          )}
          priceSuffix={t("membership.perDelivery")}
          planName={plan.name}
          recommended
          ctaLabel={t("membership.checkout.subscribe")}
          onSelect={onOpen}
          testId="membership.plan-subscription"
          ctaTestId="membership.checkout-subscription"
        />
      ) : null}
      {product.requires_selling_plan ? null : (
        <PlanCard
          label={t("membership.plans.oneTime.label")}
          title={t("membership.plans.oneTime.title")}
          description={t("membership.plans.oneTime.description")}
          price={formatCurrency(
            variant.price.amount,
            variant.price.currency_code,
          )}
          note={t("membership.plans.oneTime.note")}
          ctaLabel={t("membership.checkout.buyOnce")}
          onSelect={onOpen}
          testId="membership.plan-oneTime"
          ctaTestId="membership.checkout-oneTime"
        />
      )}
    </Box>
  );
}
